import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import { usePreviewMode } from '../hooks/usePreviewMode';
import { appendUtm } from '../lib/utm';
import { EditableText } from './inline/EditableText';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REGISTRATION_URL = 'https://utconferences.eventsair.com/cee/registration/Site/Register';

// Map URL paths to visibility page keys — cards linking to a hidden page auto-hide.
const URL_TO_PAGE_KEY = {
  '/': 'home',
  '/program': 'program',
  '/speakers': 'speakers',
  '/sponsorship': 'sponsorship',
  '/golf-outing': 'golf',
  '/golf': 'golf',
};

// Default images mapped by card title (used as fallback when CMS doesn't provide images)
const defaultImages = {
  'attend': '/assets/images/attend-section.jpg',
  'exhibit': '/assets/images/exhibit-section.jpg',
  'sponsor': '/assets/images/sponsor-section.jpg',
  'golf outing': '/assets/images/golf-section.jpg',
  'charity golf tournament': '/assets/images/golf-section.jpg',
  'golf': '/assets/images/golf-section.jpg',
};

// Default action cards (complete fallback if CMS data not available)
const defaultCards = [
  {
    title: 'Attend',
    subtitle: 'Thank you for your interest in WNC',
    description: 'Registration is now open! Join hundreds of nuclear energy professionals from around the world.',
    cta: { label: 'Register Now', url: REGISTRATION_URL },
    image: '/assets/images/attend-section.jpg',
    type: 'navigate'
  },
  {
    title: 'Exhibit',
    subtitle: 'Ready to book your spot for WNC 26?',
    description: 'Be a part of the premier nuclear energy event showcasing the latest innovations, technologies, and solutions.',
    cta: { label: 'Learn More', url: '/sponsorship#exhibits' },
    image: '/assets/images/exhibit-section.jpg',
    type: 'navigate'
  },
  {
    title: 'Sponsor',
    subtitle: 'Maximize your company impact',
    description: 'Reserving booths now. Gain visibility in front of key nuclear energy professionals, policymakers, and industry leaders from around the globe.',
    cta: { label: 'Sponsorships', url: '/sponsorship' },
    image: '/assets/images/sponsor-section.jpg',
    type: 'navigate'
  },
  {
    title: 'Charity Golf Tournament',
    subtitle: 'Hosted by the Congressional Energy Engagement Foundation',
    description: 'Format is scramble. Date, host course, and price per team coming soon. Join us for a day of golf and networking in support of a great cause.',
    cta: { label: 'Express Interest', url: '/golf-outing' },
    image: '/assets/images/golf-section.jpg',
    type: 'navigate'
  }
];

const ActionSections = ({ content, sectionId: pageSectionId }) => {
  const navigate = useNavigate();
  const { preview } = usePreviewMode();
  const [visibility, setVisibility] = useState(null);
  const scope = (path) => ({ type: 'homepage_section', sectionId: pageSectionId, path });

  useEffect(() => {
    let ignore = false;
    fetch(`${API_URL}/api/content/visibility`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!ignore && d) setVisibility(d); })
      .catch(() => {});
    return () => { ignore = true; };
  }, []);

  const isCardVisible = (card) => {
    if (preview) return true;
    if (!visibility) return true;
    const url = card?.cta?.url || card?.link || '';
    if (!url || !url.startsWith('/')) return true;
    // Strip hash for lookup (e.g. /sponsorship#exhibits → /sponsorship)
    const pathOnly = url.split('#')[0].split('?')[0];
    const pageKey = URL_TO_PAGE_KEY[pathOnly];
    if (!pageKey) return true;
    const pageSettings = visibility.pages?.[pageKey];
    if (pageSettings && pageSettings.visible === false) return false;
    return true;
  };

  // Use CMS cards if available, otherwise use defaults
  // Merge CMS cards with default images when image is missing
  const rawSections = content?.cards 
    ? content.cards.map(card => ({
        ...card,
        image: card.image || defaultImages[card.title?.toLowerCase()] || '/assets/images/attend-section.jpg'
      }))
    : defaultCards;

  const sections = rawSections.filter(isCardVisible);

  const handleClick = (section) => {
    const url = section.cta?.url || section.link;
    const type = section.type || (url?.startsWith('mailto:') ? 'mailto' : url?.startsWith('#') ? 'scroll' : 'navigate');
    
    if (type === 'scroll') {
      const targetId = url?.replace('#', '') || 'registration-section';
      const element = document.getElementById(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (type === 'mailto') {
      window.location.href = url;
    } else if (type === 'navigate') {
      if (url?.startsWith('/')) {
        navigate(url);
      } else {
        // Tag every action card CTA with its card title so we know which section drove the click
        const slug = (section.title || 'card').toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        window.open(appendUtm(url, { utm_medium: 'action_card', utm_content: slug }), '_blank');
      }
    }
  };

  return (
    <section className="py-0">
      {sections.map((section, index) => {
        const ctaLabel = section.cta?.label || section.cta || 'Learn More';
        const sectionId = section.title?.toLowerCase().replace(/\s+/g, '-') || `section-${index}`;
        
        return (
          <div
            key={index}
            id={`${sectionId}-section`}
            className="relative min-h-[500px] flex items-center overflow-hidden"
          >
            {/* Background Image */}
            <div className="absolute inset-0 z-0">
              <img
                src={section.image}
                alt={section.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-[#00457f]/95 via-[#00457f]/85 to-transparent"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-16">
              <div className="max-w-2xl">
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                  <EditableText as="span" scope={scope(`cards.${index}.title`)} value={section.title} multiline={false}>{section.title}</EditableText>
                </h2>
                <h3 className="text-xl md:text-2xl font-semibold mb-4 bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 text-transparent bg-clip-text">
                  <EditableText as="span" scope={scope(`cards.${index}.subtitle`)} value={section.subtitle} multiline={false}>{section.subtitle}</EditableText>
                </h3>
                <p className="text-lg text-gray-200 mb-8 leading-relaxed">
                  <EditableText as="span" scope={scope(`cards.${index}.description`)} value={section.description}>{section.description}</EditableText>
                </p>
                <Button
                  size="lg"
                  onClick={() => handleClick(section)}
                  className="bg-gradient-to-r from-yellow-500 via-amber-500 to-yellow-600 hover:from-yellow-600 hover:via-amber-600 hover:to-yellow-700 text-white text-base font-bold px-8 py-5 rounded-full transition-all duration-300 transform hover:scale-105 shadow-xl relative overflow-hidden"
                  style={{
                    boxShadow: '0 10px 40px rgba(245, 158, 11, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.3)'
                  }}
                >
                  <EditableText as="span" scope={scope(`cards.${index}.cta.label`)} value={section.cta?.label} multiline={false}>{ctaLabel}</EditableText>
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </section>
  );
};

export default ActionSections;